(() => {
  if (customElements.get('frequently-bought-together')) return;

  const engine = window.CharterhouseRecommendationEngine;
  const words = engine.normalize;
  const has = engine.hasAny;

  class FrequentlyBoughtTogether extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.items = this.querySelector('[data-items]');
      this.total = this.querySelector('[data-total]');
      this.button = this.querySelector('[data-add]');
      this.status = this.querySelector('[data-status]');
      this.variants = JSON.parse(this.querySelector('[data-main-variants]')?.textContent || '[]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.dataset.currency || 'GBP'
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-pair] input[type="checkbox"]')) this.updateTotal();
        const select = event.target.closest('[data-fbt-variant-select]');
        if (select) {
          const card = select.closest('[data-pair]');
          const option = select.selectedOptions[0];
          card.dataset.variantId = option.value;
          card.dataset.price = option.dataset.price;
          card.querySelector('.ch-fbt__price').textContent = this.money.format(Number(option.dataset.price) / 100);
          this.refreshDelivery(card, option.value);
          this.updateTotal();
        }
      });
      this.button.addEventListener('click', () => this.addSelected());
      this.addEventListener('click', event => {
        if (event.target.closest('[data-product-link]')) event.stopPropagation();
      });
      document.addEventListener('change', () => setTimeout(() => this.syncMainVariant(), 0));
      this.mainVariantId = Number(this.querySelector('[data-main-variant]')?.dataset.mainVariant);
      this.alignCuratedVariants();
      this.items.style.setProperty('--fbt-count', String(Math.min(4, this.items.children.length)));
      this.syncMainVariant();
      this.updateTotal();
      if (!this.querySelector('[data-pair]') && (this.dataset.auto === 'true' || this.dataset.compatibleFallback === 'true')) {
        this.hidden = true;
        this.dataset.mode = 'suggested';
        this.querySelector('[data-heading]').textContent = this.dataset.suggestedHeading;
        this.querySelector('[data-note]').textContent = 'Suggested products for this setup.';
        const load = () => {
          if (this.isConnected) this.loadSuggestions();
        };
        if (document.readyState === 'complete') load();
        else window.addEventListener('load', load, {once: true});
      } else if (!this.querySelector('[data-pair]')) this.hidden = true;
      else {
        this.initRatings();
        this.announcePairs();
      }
    }

    announcePairs() {
      const productIds = [...this.querySelectorAll('[data-pair][data-product-id]')]
        .map(card => card.dataset.productId)
        .filter(Boolean);
      document.dispatchEvent(new CustomEvent('charterhouse:paired-products', {
        detail: {productId: this.dataset.productId, productIds}
      }));
    }

    updateTotal() {
      let cents = Number(this.querySelector('.ch-fbt__item--main [data-price]')?.dataset.price || 0);
      this.querySelectorAll('[data-pair]').forEach(card => {
        if (card.querySelector('input').checked) cents += Number(card.dataset.price || 0);
      });
      this.total.textContent = this.money.format(cents / 100);
    }

    syncMainVariant() {
      const id = Number(document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value);
      const variant = this.variants.find(item => Number(item.id) === id);
      if (!variant) return;
      this.mainVariantId = id;
      const price = this.querySelector('.ch-fbt__item--main [data-price]');
      price.dataset.price = String(variant.price);
      price.textContent = this.money.format(Number(variant.price) / 100);
      this.refreshDelivery(this.querySelector('.ch-fbt__item--main'), id);
      this.updateTotal();
    }

    alignCuratedVariants() {
      const sourceModel = words(this.dataset.productTitle).match(/\bxr(15|30)/)?.[1];
      if (!sourceModel) return;
      this.querySelectorAll('[data-pair] [data-fbt-variant-select]').forEach(select => {
        const modelled = [...select.options].filter(option => /\bxr(15|30)/i.test(option.textContent));
        if (!modelled.length) return;
        const matching = modelled.filter(option => new RegExp(`\\bxr${sourceModel}`, 'i').test(option.textContent));
        const card = select.closest('[data-pair]');
        if (!matching.length) { card.remove(); return; }
        for (const option of modelled) if (!matching.includes(option)) option.remove();
        select.value = matching[0].value;
        card.dataset.variantId = matching[0].value;
        card.dataset.price = matching[0].dataset.price;
        card.querySelector('.ch-fbt__price').textContent = this.money.format(Number(matching[0].dataset.price) / 100);
        this.refreshDelivery(card, matching[0].value);
        if (select.options.length === 1) select.remove();
      });
    }

    eligible(item) {
      if (!item || !item.available || Number(item.id) === Number(this.dataset.productId)) return false;
      if (this.querySelector(`[data-pair][data-product-id="${item.id}"]`)) return false;
      if (!item.variants || !this.compatibleVariants(item).length || item.requires_selling_plan) return false;
      const source = engine.fromDataset(this.dataset);
      const candidate = engine.fromProduct(item);
      if (engine.isLiveStock(source)) return false;
      return engine.isCompatible(source, candidate);
    }

    compatibleVariants(item) {
      const variants = (item.variants || []).filter(variant => variant.available);
      const sourceModel = words(this.dataset.productTitle).match(/\bxr(15|30)/)?.[1];
      if (!sourceModel) return variants;
      const modelled = variants.filter(variant => /\bxr(15|30)/i.test(variant.title || ''));
      return modelled.length ? modelled.filter(variant => new RegExp(`\\bxr${sourceModel}`, 'i').test(variant.title)) : variants;
    }

    refreshDelivery(card, variantId) {
      const delivery = card?.querySelector('.ch-fbt__delivery');
      if (!delivery || delivery.dataset.variants === String(variantId)) return;
      delivery.dataset.variants = String(variantId);
      delivery.classList.add('gib-loading');
      delivery.replaceChildren();
      const spinner = document.createElement('i');
      spinner.className = 'fa-light fa-spinner-third fa-spin';
      spinner.setAttribute('aria-hidden', 'true');
      delivery.append(spinner);
    }

    addReview(details, item) {
      if (this.dataset.showReviews !== 'true') return;
      const wrapper = document.createElement('span');
      wrapper.className = 'ch-fbt__reviews';
      const rating = document.createElement('span');
      rating.className = 'ruk_rating_snippet';
      rating.dataset.sku = [item.handle, ...(item.variants || []).map(variant => variant.sku).filter(Boolean), item.id].join(';');
      wrapper.append(rating);
      details.append(wrapper);
    }

    addDelivery(details, item, variant) {
      if (this.dataset.showDelivery !== 'true' || this.dataset.gibEnabled !== 'true') return;
      const delivery = document.createElement('span');
      delivery.className = 'ch-fbt__delivery gib-container gib-loading';
      delivery.dataset.variants = String(variant.id);
      delivery.dataset.product = String(item.id);
      delivery.dataset.icon = 'true';
      const spinner = document.createElement('i');
      spinner.className = 'fa-light fa-spinner-third fa-spin';
      spinner.setAttribute('aria-hidden', 'true');
      delivery.append(spinner);
      details.append(delivery);
    }

    initRatings() {
      if (this.dataset.showReviews !== 'true') return;
      if (typeof window.ratingSnippet !== 'function') {
        if (!document.querySelector('script[src*="rating-snippet/dist.js"]')) {
          window.reviewsIoStore = 'charterhouse-aquatics.myshopify.com';
          const css = document.createElement('link');
          css.rel = 'stylesheet';
          css.href = 'https://widget.reviews.io/rating-snippet/dist.css';
          document.head.append(css);
          const script = document.createElement('script');
          script.src = 'https://widget.reviews.io/rating-snippet/dist.js';
          script.defer = true;
          document.head.append(script);
        }
        this.ratingAttempts = (this.ratingAttempts || 0) + 1;
        if (this.ratingAttempts <= 20) setTimeout(() => this.initRatings(), 250);
        return;
      }
      const starColor = getComputedStyle(this).getPropertyValue('--fbt-star').trim() || '#c78b16';
      window.ratingSnippet('ruk_rating_snippet', {store: window.reviewsIoStore || 'charterhouse-aquatics.myshopify.com', color: starColor, textClr: '#333', linebreak: false, usePolaris: true, text: 'Reviews', lang: 'en', mode: 'default', showEmptyStars: false});
    }

    async loadSuggestions() {
      if (this.dataset.auto === 'true') {
        try {
          const firstCharacter = this.dataset.productHandle.charAt(0);
          let purchaseIndexUrl = this.dataset.purchaseIndexUrlThree;
          if (/^[a-i]$/.test(firstCharacter)) purchaseIndexUrl = this.dataset.purchaseIndexUrlOne;
          else if (/^[j-r]$/.test(firstCharacter)) purchaseIndexUrl = this.dataset.purchaseIndexUrlTwo;
          const response = await fetch(purchaseIndexUrl, {headers: {Accept: 'application/json'}});
          if (response.ok) {
            const index = await response.json();
            const handles = index.version === 1 ? index.pairs?.[this.dataset.productHandle] : null;
            if (await this.showCandidates(handles, 'purchased')) return;
          }
        } catch (error) { /* A verified compatibility fallback may still be available. */ }
      }
      if (this.dataset.compatibleFallback === 'true') {
        const handles = (this.dataset.compatibleHandles || '').split('|').filter(Boolean);
        await this.showCandidates(handles, 'compatible');
      }
    }

    async showCandidates(handles, mode) {
      if (!Array.isArray(handles) || handles.length < 3) return false;
      try {
        const candidates = [...new Set(handles)].slice(0, 12);
        const qualified = [];
        const seenIds = new Set();
        for (let offset = 0; offset < candidates.length && qualified.length < 3; offset += 3) {
          const products = await Promise.all(candidates.slice(offset, offset + 3).map(async handle => {
            if (typeof handle !== 'string' || !/^[a-z0-9-]+$/.test(handle)) return null;
            try {
              const root = this.dataset.rootUrl.replace(/\/$/, '');
              const result = await fetch(`${root}/products/${handle}.js`);
              return result.ok ? result.json() : null;
            } catch (error) { return null; }
          }));
          for (const item of products) {
            if (!item || !item.featured_image || !this.eligible(item) || seenIds.has(item.id)) continue;
            seenIds.add(item.id);
            qualified.push(item);
          }
        }
        if (qualified.length < 3) return false;
        const limit = Math.max(1, Math.min(3, Number(this.dataset.limit) || 3));
        for (const item of qualified.slice(0, limit)) this.items.append(this.makeCard(item, mode));
        this.querySelector('[data-heading]').textContent = mode === 'purchased' ? this.dataset.verifiedHeading : this.dataset.suggestedHeading;
        this.querySelector('[data-note]').textContent = mode === 'purchased' ? 'Frequently bought together by customers.' : 'Verified compatible additions for this setup.';
        this.items.style.setProperty('--fbt-count', String(Math.min(4, this.items.children.length)));
        this.updateTotal();
        this.initRatings();
        this.hidden = false;
        this.announcePairs();
        return true;
      } catch (error) {
        this.hidden = true;
        return false;
      }
    }

    makeCard(item, mode) {
      const variants = this.compatibleVariants(item);
      const variant = variants[0];
      const card = document.createElement('label');
      card.className = 'ch-fbt__item';
      card.dataset.pair = '';
      card.dataset.productId = String(item.id);
      card.dataset.variantId = String(variant.id);
      card.dataset.price = String(variant.price);
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = true;
      checkbox.setAttribute('aria-label', `Include ${item.title}`);
      const media = document.createElement('span');
      media.className = 'ch-fbt__image';
      if (item.featured_image) {
        const image = document.createElement('img');
        const imageUrl = new URL(item.featured_image, window.location.origin);
        if (imageUrl.hostname === 'cdn.shopify.com' || imageUrl.pathname.startsWith('/cdn/shop/')) {
          const sized = width => {
            const url = new URL(imageUrl);
            url.searchParams.set('width', String(width));
            return url.href;
          };
          image.src = sized(720);
          image.srcset = [320, 480, 720].map(width => `${sized(width)} ${width}w`).join(', ');
          image.sizes = '(max-width: 640px) 100px, (max-width: 1100px) 45vw, 25vw';
        } else image.src = imageUrl.href;
        image.alt = item.title;
        image.width = 720;
        image.height = 576;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      }
      const details = document.createElement('span');
      details.className = 'ch-fbt__details';
      const eyebrow = document.createElement('span');
      eyebrow.className = 'ch-fbt__eyebrow';
      eyebrow.textContent = mode === 'purchased' ? 'Bought with this' : 'Compatible with this';
      const title = document.createElement('span');
      title.className = 'ch-fbt__title';
      title.textContent = item.title;
      const select = document.createElement('select');
      select.className = 'ch-fbt__variant';
      select.dataset.fbtVariantSelect = '';
      select.setAttribute('aria-label', `Choose an option for ${item.title}`);
      for (const choice of variants) {
        const option = document.createElement('option');
        option.value = String(choice.id);
        option.dataset.price = String(choice.price);
        option.textContent = choice.title;
        select.append(option);
      }
      const price = document.createElement('strong');
      price.className = 'ch-fbt__price';
      price.textContent = this.money.format(Number(variant.price) / 100);
      const link = document.createElement('a');
      link.className = 'ch-fbt__link';
      link.href = item.url;
      link.dataset.productLink = '';
      link.textContent = 'View details';
      details.append(eyebrow, title);
      this.addReview(details, item);
      if (variants.length > 1) details.append(select);
      this.addDelivery(details, item, variant);
      details.append(price, link);
      card.append(checkbox, media, details);
      return card;
    }

    async addSelected() {
      this.syncMainVariant();
      const productFormVariant = Number(document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value);
      const variantId = productFormVariant || this.mainVariantId || Number(this.querySelector('[data-main-variant]')?.dataset.mainVariant);
      if (!variantId) {
        this.status.textContent = 'Choose an option for this product first.';
        return;
      }
      const items = [{id: variantId, quantity: 1}];
      this.querySelectorAll('[data-pair]').forEach(card => {
        if (card.querySelector('input').checked) items.push({id: Number(card.dataset.variantId), quantity: 1});
      });
      this.button.disabled = true;
      this.status.textContent = 'Adding to basket…';
      try {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items})
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Could not add these products.');
        }
        window.location.assign('/cart');
      } catch (error) {
        this.status.textContent = error.message;
        this.button.disabled = false;
      }
    }
  }

  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
})();
